import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listContacts } from '../../api/contacts';
import { listConversations } from '../../api/conversations';
import { useAuth } from '../../auth/AuthContext';
import './command-palette.css';

/** Every page reachable from the sidebar, flattened - kept in sync with
 * AppLayout's own nav groups by hand (small, stable list; not worth
 * threading through a shared module for this). */
const NAV_SHORTCUTS: { to: string; label: string }[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/leads', label: 'Leads' },
  { to: '/pipelines', label: 'Pipelines' },
  { to: '/tasks', label: 'Tasks' },
  { to: '/follow-ups', label: 'Follow-ups' },
  { to: '/appointments', label: 'Appointments' },
  { to: '/conversations', label: 'Inbox' },
  { to: '/channels', label: 'Channels' },
  { to: '/automation-templates', label: 'Templates' },
  { to: '/automations', label: 'Automations' },
  { to: '/automation-flows', label: 'Conversation Flows' },
  { to: '/campaigns', label: 'Campaigns' },
  { to: '/products-services', label: 'Products & Services' },
  { to: '/orders', label: 'Orders' },
  { to: '/quotations', label: 'Quotations' },
  { to: '/tags', label: 'Tags' },
  { to: '/custom-fields', label: 'Custom fields' },
];

type ResultRow =
  | { kind: 'nav'; key: string; label: string; sublabel?: string; to: string }
  | { kind: 'contact'; key: string; label: string; sublabel?: string; to: string }
  | { kind: 'conversation'; key: string; label: string; sublabel?: string; to: string };

const MIN_SEARCH_LENGTH = 2;

/**
 * Global Ctrl+K / Cmd+K command palette - the one place to jump to a page,
 * find a contact, or open a conversation without hunting through the
 * (grouped, now-collapsible) sidebar. Opened from `AppLayout`'s own keydown
 * listener and its topbar search button; this component only renders while
 * open, closes on Escape/backdrop click/selecting a result.
 */
export function CommandPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const searchActive = debounced.length >= MIN_SEARCH_LENGTH;
  const canReadContacts = hasPermission('contacts.read');
  const canReadConversations = hasPermission('conversations.read');

  const contacts = useQuery({
    queryKey: ['command-palette', 'contacts', debounced],
    queryFn: () => listContacts({ search: debounced, pageSize: 5 }),
    enabled: searchActive && canReadContacts,
  });
  const conversations = useQuery({
    queryKey: ['command-palette', 'conversations', debounced],
    queryFn: () => listConversations({ search: debounced, pageSize: 5 }),
    enabled: searchActive && canReadConversations,
  });

  const navMatches = useMemo(() => {
    if (!debounced) return [];
    const needle = debounced.toLowerCase();
    return NAV_SHORTCUTS.filter((item) => item.label.toLowerCase().includes(needle));
  }, [debounced]);

  const results: ResultRow[] = useMemo(() => {
    const rows: ResultRow[] = navMatches.map((item) => ({
      kind: 'nav',
      key: `nav:${item.to}`,
      label: item.label,
      sublabel: 'Go to page',
      to: item.to,
    }));
    for (const c of contacts.data?.items ?? []) {
      rows.push({
        kind: 'contact',
        key: `contact:${c.id}`,
        label: c.displayName,
        sublabel: c.phone ?? c.email ?? undefined,
        to: `/contacts/${c.id}`,
      });
    }
    for (const c of conversations.data?.items ?? []) {
      rows.push({
        kind: 'conversation',
        key: `conversation:${c.id}`,
        label: c.contact.displayName,
        sublabel: c.lastMessagePreview ?? c.contact.phone ?? undefined,
        to: `/conversations?conversationId=${c.id}`,
      });
    }
    return rows;
  }, [navMatches, contacts.data, conversations.data]);

  useEffect(() => {
    setHighlighted(0);
  }, [results.length]);

  function select(row: ResultRow) {
    navigate(row.to);
    onClose();
  }

  function onInputKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = results[highlighted];
      if (row) select(row);
    }
  }

  const loading = searchActive && (contacts.isFetching || conversations.isFetching);

  return (
    <div
      className="ne-modal__backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ne-command-palette" role="dialog" aria-modal="true" aria-label="Search">
        <div className="ne-command-palette__input-row">
          <span aria-hidden="true">🔍</span>
          <input
            ref={inputRef}
            className="ne-command-palette__input"
            type="text"
            placeholder="Find a contact, jump to a conversation, or go to a page…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
          />
          <kbd>Esc</kbd>
        </div>

        <div className="ne-command-palette__results">
          {!debounced ? (
            <p className="ne-command-palette__hint">
              Type a contact's name, a page name, or part of a conversation to search.
            </p>
          ) : !searchActive ? (
            <p className="ne-command-palette__hint">Keep typing…</p>
          ) : loading && results.length === 0 ? (
            <p className="ne-command-palette__hint">Searching…</p>
          ) : results.length === 0 ? (
            <p className="ne-command-palette__hint">No matches for "{debounced}".</p>
          ) : (
            <ul className="ne-command-palette__list">
              {results.map((row, i) => (
                <li key={row.key}>
                  <button
                    type="button"
                    className={
                      i === highlighted
                        ? 'ne-command-palette__row ne-command-palette__row--active'
                        : 'ne-command-palette__row'
                    }
                    onMouseEnter={() => setHighlighted(i)}
                    onClick={() => select(row)}
                  >
                    <span className="ne-command-palette__row-kind">
                      {row.kind === 'nav' ? '→' : row.kind === 'contact' ? '👤' : '💬'}
                    </span>
                    <span className="ne-command-palette__row-text">
                      <span className="ne-command-palette__row-label">{row.label}</span>
                      {row.sublabel ? (
                        <span className="ne-command-palette__row-sublabel">{row.sublabel}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
